import React, { memo } from 'react';
import {
  Box,
  Button,
  Heading,
  HStack,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react';
import { MdAdd } from 'react-icons/md';
import { formatAmount } from '@utils/numberUtils';

// Separate component for Amount Range Table
export const AmountRangeTable = memo(({
  ranges,
  country,
  beneficiaryType,
  isEditMode,
  onRangeChange,
  onAddRange,
  timeUnit = 'minutes',
  getDisplayValue,
  convertInputToMinutes
}) => {
  const isDisabledRangeChanges = true; //single flag to disable changes in all ranges
  return (
    <Box>
      <HStack justify="space-between" mb={3}>
        <Heading size="xs" color="colorPrimary">Amount Ranges</Heading>
        {!isDisabledRangeChanges && (
          <Button
            size="sm"
            leftIcon={<MdAdd />}
            colorScheme="brand"
            variant="outline"
            onClick={() => onAddRange(country, beneficiaryType)}
          >
            Add Range
          </Button>
        )}
      </HStack>
      <TableContainer
        border="1px solid"
        borderColor="whiteAlpha.300"
        borderRadius="md"
        bg="dark.500"
      >
        <Table size="sm" variant="simple">
          <Thead>
            <Tr bg="dark.600">
              <Th color="gray.300" fontSize="xs">Min Amount</Th>
              <Th color="gray.300" fontSize="xs">Max Amount</Th>
              <Th color="gray.300" fontSize="xs">ETA ({timeUnit === 'hours' ? 'hrs' : 'min'})</Th>
            </Tr>
          </Thead>
          <Tbody>
            {ranges.length === 0 ? (
              <Tr>
                <Td colSpan={3} textAlign="center" py={4}>
                  <Text color="gray.500" fontSize="sm">No ranges configured</Text>
                </Td>
              </Tr>
            ) : (
              ranges.map((range, rangeIndex) => (
                <Tr key={rangeIndex} _hover={{ bg: 'dark.600' }}>
                  <Td>
                    <Input
                      type="number"
                      step="0.01"
                      value={rangeIndex === 0 ? formatAmount(range.minReceiveAmount ?? 0) : formatAmount(range.minReceiveAmount)}
                      onChange={(e) =>
                        onRangeChange(
                          country,
                          beneficiaryType,
                          rangeIndex,
                          'minReceiveAmount',
                          parseFloat(e.target.value) || 0
                        )
                      }
                      onWheel={(e) => e.target.blur()}
                      isReadOnly={isDisabledRangeChanges} //replace with isEditMode later
                      size="sm"
                      bg="dark.400"
                      color="white"
                      border="1px solid"
                      borderColor="whiteAlpha.400"
                      _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                    />
                  </Td>
                  <Td>
                    <Input
                      type="number"
                      step="0.01"
                      value={range.maxReceiveAmount === null ? '' : formatAmount(range.maxReceiveAmount)}
                      placeholder={range.maxReceiveAmount === null ? '∞' : ''}
                      onChange={(e) =>
                        onRangeChange(
                          country,
                          beneficiaryType,
                          rangeIndex,
                          'maxReceiveAmount',
                          e.target.value === '' ? null : (parseFloat(e.target.value) || 0)
                        )
                      }
                      onWheel={(e) => e.target.blur()}
                      isReadOnly={isDisabledRangeChanges}
                      size="sm"
                      bg="dark.400"
                      color="white"
                      border="1px solid"
                      borderColor="whiteAlpha.400"
                      _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                    />
                  </Td>
                  <Td>
                    <Input
                      type="number"
                      step="0.01"
                      value={getDisplayValue ? formatAmount(getDisplayValue(range.etaInMinutes)) : formatAmount(range.etaInMinutes)}
                      onChange={(e) =>
                        onRangeChange(
                          country,
                          beneficiaryType,
                          rangeIndex,
                          'etaInMinutes',
                          convertInputToMinutes(e.target.value) || 0
                        )
                      }
                      onWheel={(e) => e.target.blur()}
                      isReadOnly={false}
                      size="sm"
                      bg="dark.400"
                      color="white"
                      border="1px solid"
                      borderColor="whiteAlpha.400"
                      _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                    />
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
});

AmountRangeTable.displayName = 'AmountRangeTable';
